import type {
  LeaveReportFilters,
  LeaveReportRow,
  LeaveReportStatusTone,
} from '../types/leaveReport';
import type { ReportPreviewRow } from '../types/report';
import { formatDisplayLabel } from './camelCaseLabel';
import { calendarDateFromApi } from './dateTime';
import { getLeaveStatusLabel } from './leaveStatus';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const EMPTY = '—';

const EMPLOYEE_KEYS = [
  'employeeName',
  'employee_name',
  'employeeFullName',
  'displayName',
  'display_name',
  'fullName',
  'name',
  'employee',
];
const EMPLOYEE_NUMBER_KEYS = ['employeeNumber', 'employeeCode'];
const DEPARTMENT_KEYS = ['departmentName', 'department', 'departmentId'];
const LEAVE_TYPE_KEYS = [
  'leaveTypeName',
  'leaveType',
  'leaveTypeCode',
  'typeName',
  'type',
];
const FROM_DATE_KEYS = ['fromDate', 'startDate', 'leaveFromDate', 'from'];
const TO_DATE_KEYS = ['toDate', 'endDate', 'leaveToDate', 'to'];
const STATUS_CODE_KEYS = [
  'statusCode',
  'leaveStatusCode',
  'workflowStatusCode',
  'status',
];
const STATUS_LABEL_KEYS = [
  'statusLabel',
  'statusName',
  'leaveStatusName',
  'leaveStatus',
];

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function firstValue(
  values: Record<string, unknown>,
  keys: string[],
): unknown {
  for (const key of keys) {
    if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
      return values[key];
    }
  }
  return null;
}

function asDisplayText(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : EMPTY;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || EMPTY;
  }
  return EMPTY;
}

function parseDateParts(
  value: string,
): { year: number; month: number; day: number } | null {
  const match = value.trim().match(DATE_ONLY);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  return { year, month, day };
}

/** Design label e.g. "01 May 2024". */
export function formatLeaveReportDateLabel(value: string): string {
  const iso = calendarDateFromApi(value) ?? value.trim();
  const parts = parseDateParts(iso);
  if (!parts) return value;
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${pad2(parts.day)} ${months[parts.month - 1]} ${parts.year}`;
}

export function formatLeaveReportDateRangeLabel(
  fromDate: string,
  toDate: string,
): string {
  return `${formatLeaveReportDateLabel(fromDate)} - ${formatLeaveReportDateLabel(toDate)}`;
}

export function createDefaultLeaveReportFilters(
  now = new Date(),
): LeaveReportFilters {
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  return {
    fromDate: `${year}-${pad2(month)}-01`,
    toDate: `${year}-${pad2(month)}-${pad2(day)}`,
    department: '',
  };
}

export function validateLeaveReportDateRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!parseDateParts(fromDate.trim())) {
    return 'Use a YYYY-MM-DD from date.';
  }
  if (!parseDateParts(toDate.trim())) {
    return 'Use a YYYY-MM-DD to date.';
  }
  if (toDate.trim() < fromDate.trim()) {
    return 'To date cannot be earlier than from date.';
  }
  return null;
}

export function leaveRegisterApiFilters(
  fromDate: string,
  toDate: string,
  department: string,
): { fromDate: string; toDate: string; departmentId?: string } | null {
  if (validateLeaveReportDateRange(fromDate, toDate)) return null;
  const filters: {
    fromDate: string;
    toDate: string;
    departmentId?: string;
  } = {
    fromDate: fromDate.trim(),
    toDate: toDate.trim(),
  };
  const dept = department.trim();
  if (/^\d+$/.test(dept) && Number(dept) > 0) {
    filters.departmentId = dept;
  }
  return filters;
}

function resolveStatus(values: Record<string, unknown>): {
  label: string;
  code: string | null;
  tone: LeaveReportStatusTone;
} {
  const rawCode = asDisplayText(firstValue(values, STATUS_CODE_KEYS));
  const code =
    rawCode !== EMPTY ? rawCode.trim().toUpperCase().replace(/\s+/g, '_') : null;
  const labelFromCode = code ? getLeaveStatusLabel(code) : null;
  const labelText = asDisplayText(firstValue(values, STATUS_LABEL_KEYS));

  let label = EMPTY;
  if (labelFromCode && labelFromCode !== 'Status unavailable') {
    label = labelFromCode;
  } else if (labelText !== EMPTY) {
    label = labelText;
  } else if (code) {
    label = code
      .split('_')
      .map(part => part.charAt(0) + part.slice(1).toLowerCase())
      .join(' ');
  }

  const normalized = (code ?? label).toUpperCase().replace(/\s+/g, '_');
  if (
    normalized.includes('APPROVED') ||
    normalized === 'APPROVE' ||
    /^approved$/i.test(label)
  ) {
    return { label: label === EMPTY ? 'Approved' : label, code, tone: 'approved' };
  }
  if (
    normalized.includes('PENDING') ||
    normalized.includes('SUBMITTED') ||
    /^pending$/i.test(label)
  ) {
    return { label: label === EMPTY ? 'Pending' : label, code, tone: 'pending' };
  }
  if (
    normalized.includes('REJECTED') ||
    normalized.includes('REJECT') ||
    /^rejected$/i.test(label)
  ) {
    return { label: label === EMPTY ? 'Rejected' : label, code, tone: 'rejected' };
  }

  return { label, code, tone: 'neutral' };
}

export function mapLeaveReportRows(rows: ReportPreviewRow[]): LeaveReportRow[] {
  return rows.map((row, index) => {
    const values = row.values;
    const employeeName = asDisplayText(firstValue(values, EMPLOYEE_KEYS));
    const employeeNumber = asDisplayText(
      firstValue(values, EMPLOYEE_NUMBER_KEYS),
    );
    const employee = employeeName !== EMPTY ? employeeName : employeeNumber;

    const fromRaw = firstValue(values, FROM_DATE_KEYS);
    const toRaw = firstValue(values, TO_DATE_KEYS);
    const fromDate = calendarDateFromApi(fromRaw) ?? asDisplayText(fromRaw);
    const toDate = calendarDateFromApi(toRaw) ?? asDisplayText(toRaw);
    const status = resolveStatus(values);
    const departmentValue = firstValue(values, DEPARTMENT_KEYS);

    return {
      rowKey: row.rowKey || `leave-report-${index}`,
      employee,
      leaveType: formatDisplayLabel(
        asDisplayText(firstValue(values, LEAVE_TYPE_KEYS)),
        EMPTY,
      ),
      fromDate: fromDate === EMPTY ? '' : fromDate,
      fromLabel: fromDate === EMPTY ? EMPTY : formatLeaveReportDateLabel(fromDate),
      toDate: toDate === EMPTY ? '' : toDate,
      toLabel: toDate === EMPTY ? EMPTY : formatLeaveReportDateLabel(toDate),
      status: status.label,
      statusCode: status.code,
      statusTone: status.tone,
      department:
        departmentValue === null ? null : asDisplayText(departmentValue),
    };
  });
}

export function filterLeaveReportRows(
  rows: LeaveReportRow[],
  filters: Pick<LeaveReportFilters, 'department'>,
): LeaveReportRow[] {
  const department = filters.department.trim().toLowerCase();
  if (!department) return rows;
  return rows.filter(
    row =>
      row.department != null &&
      row.department.trim().toLowerCase() === department,
  );
}

export function summarizeLeaveReportRows(rows: LeaveReportRow[]): {
  totalLeaves: number;
  approved: number;
  pending: number;
  rejected: number;
} {
  let approved = 0;
  let pending = 0;
  let rejected = 0;
  for (const row of rows) {
    if (row.statusTone === 'approved') approved += 1;
    else if (row.statusTone === 'pending') pending += 1;
    else if (row.statusTone === 'rejected') rejected += 1;
  }
  return {
    totalLeaves: rows.length,
    approved,
    pending,
    rejected,
  };
}
